import React, { useState, useContext } from "react";

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import TextField from "@material-ui/core/TextField";
import Box from "@material-ui/core/Box";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import Grid from "@material-ui/core/Grid";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import { pink, green, orange, lightBlue, red, grey } from "@material-ui/core/colors";

// core components
import GridItem from "components/Grid/GridItem.js";
import GridContainer from "components/Grid/GridContainer.js";
import CloudImage from "components/CloudImage/CloudImage";

import AuthService from "../../AuthService";
import { AppContext } from "../../AppContext";

import IconIa from "../../img/icon_ia.svg";

/** Opens social profile; raw may be a full URL or a handle (with or without @). */
function socialProfileUrl(network, raw) {
  if (!raw || typeof raw !== "string") return null;
  const v = raw.trim();
  if (!v) return null;
  if (/^https?:\/\//i.test(v)) return v;

  const slug = v.replace(/^@+/, "").trim();
  if (!slug) return null;

  const enc = encodeURIComponent(slug);
  switch (network) {
    case "facebook":
      return `https://www.facebook.com/${enc}`;
    case "instagram":
      return `https://www.instagram.com/${enc}/`;
    case "tiktok":
      return `https://www.tiktok.com/@${enc}`;
    case "twitter":
      return `https://x.com/${enc}`;
    default:
      return null;
  }
}

const NOT_AI_THRESHOLD = 0.25;
const GENERATED_BY_AI_THRESHOLD = 0.75;

/** Chip style for AI generation score (0–1); null → pending/unknown. */
function getAiGenerationChipProps(score) {
  if (typeof score !== "number" || Number.isNaN(score)) {
    return {
      label: "N/A",
      background: grey[500],
      color: "#FFF",
    };
  }

  const label = `${Math.round(score * 100)}%`;
  if (score < NOT_AI_THRESHOLD) {
    return { label, background: green[500], color: "#FFF" };
  }
  if (score > GENERATED_BY_AI_THRESHOLD) {
    return { label, background: red[500], color: "#FFF" };
  }
  return { label, background: orange[500], color: "#FFF" };
}

const styles = {
  cardCategoryWhite: {
    "&,& a,& a:hover,& a:focus": {
      color: "rgba(255,255,255,.62)",
      margin: "0",
      fontSize: "14px",
      marginTop: "0",
      marginBottom: "0",
    },
    "& a,& a:hover,& a:focus": {
      color: "#FFFFFF",
    },
  },
  cardTitleWhite: {
    color: "#FFFFFF",
    marginTop: "0px",
    minHeight: "auto",
    fontWeight: "300",
    fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
    marginBottom: "3px",
    textDecoration: "none",
    "& small": {
      color: "#777",
      fontSize: "65%",
      fontWeight: "400",
      lineHeight: "1",
    },
  },
  idContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
  idImg: {
    width: "350px",
    height: "500px",
    objectFit: "cover",
    cursor: "pointer",
  },
  postDetails: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    fontSize: "22px",
    marginBottom: "10px",
    position: "relative",
  },
  thumb: {
    position: "absolute",
    top: "10px",
    right: "10px",
    width: "60px",
    height: "60px",
    borderRadius: "50%",
  },
  posTopRight: {
    position: "absolute",
    top: "10px",
    right: "10px",
  },
  colorOrange: {
    color: "#ffa500",
  },
  colorSkyBlue: {
    color: "#87ceeb",
  },
  colorRed: {
    color: "#b20000",
  },
  warningButton: {
    marginTop: "20px",
    marginLeft: "20px",
    color: "#FFF",
    background: orange[500],
  },
  dangerButton: {
    marginTop: "20px",
    marginLeft: "20px",
    color: "#FFF",
    background: pink[500],
  },
  successButton: {
    marginTop: "20px",
    color: "#FFF",
    background: green[500],
  },
  actionButton: {
    marginLeft: "20px",
    color: "#FFF",
    background: lightBlue[500],
  },
  orangeSwitchButton: {
    marginLeft: "20px",
    color: "#FFF",
    backgroundColor: orange[600],
    "&:hover": {
      backgroundColor: orange[800],
    },
  },
  profileMetaRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "stretch",
    gap: "10px",
    marginTop: 14,
    maxWidth: "100%",
  },
  profileMeta: {
    display: "inline-flex",
    flexWrap: "wrap",
    alignItems: "stretch",
    alignSelf: "flex-start",
    gap: "10px",
    padding: "12px 14px",
    backgroundColor: "#f4f6f9",
    borderRadius: 10,
    border: "1px solid #e2e6ef",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.8)",
    maxWidth: "100%",
    minWidth: "150px",
    boxSizing: "border-box",
  },
  profileMetaItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    flex: "0 1 auto",
    minWidth: "150px",
    maxWidth: "100%",
    padding: "8px 12px",
    borderRadius: 8,
    backgroundColor: "#fff",
    border: "1px solid #dce3ee",
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
    boxSizing: "border-box",
  },
  profileMetaLabel: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: "#6b7280",
    marginBottom: 4,
    flexShrink: 0,
  },
  profileMetaValue: {
    fontSize: 14,
    color: "#1f2937",
    minWidth: 0,
    maxWidth: "100%",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },
  profileMetaItemLink: {
    cursor: "pointer",
    textDecoration: "none",
    color: "inherit",
    transition: "background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease",
    "&:hover": {
      backgroundColor: "#eef4ff",
      borderColor: "#a8c0f0",
      boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
    },
    "&:focus": {
      outline: "2px solid #5b8def",
      outlineOffset: 2,
    },
  },
  gridButton: {
    width: "350px",
    height: "100%",
    background: "#EFEFEF",
    color: "#333",
    borderRadius: "20px",
  },
  descriptionTextField: {
    width: "100%",
  },
  icon: {
    marginRight: "10px",
    height: "30px",
  },
  displayName: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "flex-start",
    alignItems: "center",
    gap: "8px",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
  },
  formControl: {
    width: "100%",
    marginBottom: "20px",
  },
  errorMessage: {
    margin: "10px 0",
    color: "#b20000",
    fontSize: "14px",
  },
  scrollContainer: {
    display: "flex",
    flexDirection: "row",
    overflowX: "auto",
    flexWrap: "nowrap",
    padding: "0 1rem",
    scrollbarWidth: "thin",
    "&::-webkit-scrollbar": {
      height: "6px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#aaa",
      borderRadius: "10px",
    },
  },
  scrollItem: {
    flex: "0 0 auto",
    width: "350px",
    height: "500px",
    marginRight: "16px",
  },
};

const useStyles = makeStyles(styles);

export default function UserIdentity(props) {
  const context = useContext(AppContext);
  const [visible, setVisible] = useState(true);
  const [postsLoaded, setPostsLoaded] = useState(false);
  const [posts, setPosts] = useState([]);
  const [identity, setIdentity] = useState(props.identity);
  const [toggle, setToggle] = useState(false);
  const [responseSelected, setResponseSelected] = useState("");
  const [responseDetails, setResponseDetails] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const classes = useStyles();
  const { godFatheridentity } = props;

  const status = {
    identityNotYetAccepted: (
      <label className={classes.colorOrange}>Pending</label>
    ),
    identityNotAccepted: <label className={classes.colorRed}>Refused</label>,
    identityAccepted: <label className={classes.colorSkyBlue}>Accepted</label>,
  };

  const resetResponses = () => {
    setResponseSelected("");
    setResponseDetails("");
  };

  const handleResponseChange = (e) => {
    setResponseSelected(e.target.value);
  };

  const handleDeleteUser = () => {
    const authService = new AuthService();
    authService.setUrl(context.apiUrl + context.usersHonoPath);
    authService.request("delete", { userId: identity.userId }, () => {});
    setVisible(false);
  };

  const sendResponse = (identityId, status) => {
    if (
      status == "identityNotAccepted" &&
      (!responseSelected ||
        (responseSelected === "personalizedResponse" && !responseDetails))
    ) {
      setErrorMessage("Please enter a response");
      return;
    }

    identity.status = status;
    identity.response =
      responseSelected === "personalizedResponse"
        ? responseDetails
        : responses.filter((response) => response.value == responseSelected)[0]
            ?.label || "";
    setIdentity(identity);
    setToggle(!toggle);

    const authService = new AuthService();
    authService.setUrl(context.apiUrl + context.identitiesPath);
    authService.request(
      "put",
      {
        identityId,
        status,
        response: responseSelected,
        responseDetails:
          responseSelected === "personalizedResponse" ? responseDetails : "",
      },
      () => {
        responsesActions[responseSelected]();
        resetResponses();
      }
    );
  };

  const handleSuspendUser = () => {
    const authService = new AuthService();
    authService.setUrl(context.apiUrl + context.usersHonoPath + "suspend");
    authService.request(
      "put",
      { userId: identity.userId, suspend: true },
      () => {}
    );
    setVisible(false);
  };

  const handleSwitchAi = (identity) => {
    identity.ia = true;
    setIdentity(identity);
    setToggle(!toggle);

    const authService = new AuthService();
    authService.setUrl(context.apiUrl + context.usersHonoPath + "ia");
    authService.request(
      "put",
      {
        userId: identity.userId,
        value: true,
      },
      () => {}
    );
  };

  const handleSwitchNormal = (identity) => {
    identity.ia = false;
    setIdentity(identity);
    setToggle(!toggle);

    const authService = new AuthService();
    authService.setUrl(context.apiUrl + context.usersHonoPath + "ia");
    authService.request(
      "put",
      {
        userId: identity.userId,
        value: false,
      },
      () => {}
    );
  };

  const showPosts = () => {
    const authService = new AuthService();
    authService.setUrl(context.apiUrl + context.postsHonoPath + "user");
    authService.request(
      "get",
      {
        key: props.identity.userId.split("/")[1],
        start: 0,
        end: 20,
        type: "all",
        status: "all",
        categories: JSON.stringify([
          "contentCategories/18834037",
          "contentCategories/18834109",
          "contentCategories/18834133",
          "contentCategories/18834230",
          "contentCategories/18834312",
          "contentCategories/19504187",
          "contentCategories/24357943",
          "contentCategories/24358349",
          "contentCategories/24358763",
          "contentCategories/24359027",
          "contentCategories/24359368",
          "contentCategories/24361001",
          "contentCategories/2535005",
          "contentCategories/2535135",
          "contentCategories/2535284",
          "contentCategories/2535392",
          "contentCategories/2535500",
          "contentCategories/2535630",
          "contentCategories/2535682",
          "contentCategories/2535752",
          "contentCategories/2535816",
          "contentCategories/3181731",
          "contentCategories/3181942",
          "contentCategories/3182674",
          "contentCategories/3184320",
          "contentCategories/3588371",
          "contentCategories/3588397",
          "contentCategories/3588491",
          "contentCategories/3588523",
          "contentCategories/3588563",
          "contentCategories/8488870",
          "contentCategories/8488888",
          "contentCategories/8488920",
          "contentCategories/8488968",
          "contentCategories/8488984",
          "contentCategories/8488992",
        ]),
        seeMine: false,
      },
      (response) => {
        setPosts(response.data);
        setPostsLoaded(true);
      }
    );
  };

  const responses = [
    {
      value: "mustBeLegalAgeToHaveCreatorAccount",
      label: "You must be of legal age to have a creator account on Uncove.",
    },
    {
      value: "yourIdentityDocumentsAreIllegible",
      label:
        "Your identity documents are illegible. Please take a clear photo of your documents along with a selfie of you with your face visible.",
    },
    {
      value: "clearSelfieHoldingYourIdentityDocument",
      label:
        "A clear selfie with your face visible holding your identity document is required for account verification.",
    },
    {
      value: "identityDocumentsMustMatchYourProfile",
      label:
        "The identity documents must match the person represented in the content published on your profile.",
    },
    {
      value: "documentMustBeOfficialIdentityDocument",
      label:
        "The document must be an official identity document (driver's license, passport, or national ID card).",
    },
    {
      value: "fraudulentIdentityDocuments",
      label:
        "Your identity documents appear to be fraudulent. Please try with another document.",
    },
    {
      value: "pleaseAddPostsBeforeIdentityDocument",
      label:
        "Please add at least 3 public posts and 3 private posts before submitting your identity documents.",
    },
    {
      value: "contentFullGenerated",
      label:
        "Only content entirely generated by AI is accepted, or content modified by AI, provided it belongs to the creator and faithfully represents their identity.",
    },
    {
      value: "aiContentMustRepresentAdult",
      label:
        "AI-generated content must represent a person who appears to be an adult.",
    },
    {
      value: "profileNotEligibleForCreator",
      label:
        "Your profile is not eligible to become a creator on Uncove. After verification, we found that you do not meet the required criteria to create a creator account. To be eligible, you must have at least 500 followers and offer engaging content. We invite you to continue developing your community and come back to us when you think you meet these criteria. Thank you for your understanding.",
    },
    {
      value: "countryNotOpenOnUncove",
      label: "Your country is not open on Uncove",
    },
    {
      value: "personalizedResponse",
      label: "Personalized response",
    },
  ];

  const responsesActions = {
    mustBeLegalAgeToHaveCreatorAccount: handleDeleteUser,
    yourIdentityDocumentsAreIllegible: () => {},
    clearSelfieHoldingYourIdentityDocument: () => {},
    identityDocumentsMustMatchYourProfile: () => {},
    documentMustBeOfficialIdentityDocument: () => {},
    personalizedResponse: () => {},
  };

  const profilePicUrl =
    identity.userPicture &&
    (identity.userPicture.small ||
      identity.userPicture.medium ||
      identity.userPicture.normal);

  const uncovePublicProfileUrl =
    identity.userName &&
    `${context.domain}/${encodeURIComponent(identity.userName)}`;

  const hasProfileMeta =
    identity.userStatus ||
    identity.userName ||
    identity.facebookName ||
    identity.instagramName ||
    identity.tiktokName ||
    identity.twitterName;

  const aiGenerationChip = getAiGenerationChipProps(identity.generatedByAi);

  return (
    <>
      {visible && (
        <Box padding={1}>
          <GridContainer>
            <GridItem xs={12} sm={12} md={12}>
              <div className={classes.postDetails}>
                <div className={classes.posTopRight} key={identity.status}>
                  {
                    status[
                      identity.status
                        ? identity.status
                        : "identityNotYetAccepted"
                    ]
                  }
                </div>
                <div className={classes.displayName}>
                  {profilePicUrl && (
                    <img
                      src={profilePicUrl}
                      alt=""
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        marginRight: 8,
                        objectFit: "cover",
                      }}
                    />
                  )}
                  {identity.ia && <img className={classes.icon} src={IconIa} />}
                  {identity.isModel ? (
                    <Chip
                      label="Model"
                      style={{
                        background: "#d62546",
                        color: "#FFF",
                        marginRight: "5px",
                      }}
                    />
                  ) : (
                    <Chip
                      label="Fan"
                      style={{
                        background: "#0088e8",
                        color: "#FFF",
                        marginRight: "5px",
                      }}
                    />
                  )}
                  {identity.firstName} {identity.lastName} ({identity.userName})
                  {!identity.ia && identity.isModel && (
                    <Button
                      className={classes.orangeSwitchButton}
                      onClick={() => {
                        handleSwitchAi(identity);
                      }}
                      variant="contained"
                    >
                      Switch IA
                    </Button>
                  )}
                  {identity.ia && identity.isModel && (
                    <Button
                      className={classes.orangeSwitchButton}
                      onClick={() => {
                        handleSwitchNormal(identity);
                      }}
                      variant="contained"
                    >
                      Switch normal
                    </Button>
                  )}
                </div>
                <Box className={classes.profileMetaRow}>
                  {hasProfileMeta && (
                    <Box className={classes.profileMeta}>
                      {identity.userStatus && (
                        <Box className={classes.profileMetaItem}>
                          <span className={classes.profileMetaLabel}>Statut</span>
                          <span className={classes.profileMetaValue}>
                            {identity.userStatus}
                          </span>
                        </Box>
                      )}
                      {uncovePublicProfileUrl && (
                        <Box
                          component="a"
                          href={uncovePublicProfileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`${classes.profileMetaItem} ${classes.profileMetaItemLink}`}
                        >
                          <span className={classes.profileMetaLabel}>
                            Uncove
                          </span>
                          <span className={classes.profileMetaValue}>
                            {identity.userName}
                          </span>
                        </Box>
                      )}
                      {identity.facebookName && (
                        <Box
                          component="a"
                          href={socialProfileUrl("facebook", identity.facebookName)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`${classes.profileMetaItem} ${classes.profileMetaItemLink}`}
                        >
                          <span className={classes.profileMetaLabel}>Facebook</span>
                          <span className={classes.profileMetaValue}>
                            {identity.facebookName}
                          </span>
                        </Box>
                      )}
                      {identity.instagramName && (
                        <Box
                          component="a"
                          href={socialProfileUrl("instagram", identity.instagramName)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`${classes.profileMetaItem} ${classes.profileMetaItemLink}`}
                        >
                          <span className={classes.profileMetaLabel}>Instagram</span>
                          <span className={classes.profileMetaValue}>
                            {identity.instagramName}
                          </span>
                        </Box>
                      )}
                      {identity.tiktokName && (
                        <Box
                          component="a"
                          href={socialProfileUrl("tiktok", identity.tiktokName)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`${classes.profileMetaItem} ${classes.profileMetaItemLink}`}
                        >
                          <span className={classes.profileMetaLabel}>TikTok</span>
                          <span className={classes.profileMetaValue}>
                            {identity.tiktokName}
                          </span>
                        </Box>
                      )}
                      {identity.twitterName && (
                        <Box
                          component="a"
                          href={socialProfileUrl("twitter", identity.twitterName)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`${classes.profileMetaItem} ${classes.profileMetaItemLink}`}
                        >
                          <span className={classes.profileMetaLabel}>Twitter / X</span>
                          <span className={classes.profileMetaValue}>
                            {identity.twitterName}
                          </span>
                        </Box>
                      )}
                    </Box>
                  )}
                  <Box className={classes.profileMeta}>
                    <Box className={classes.profileMetaItem}>
                      <span className={classes.profileMetaLabel}>
                        AI Generation
                      </span>
                      <Chip
                        size="small"
                        label={aiGenerationChip.label}
                        style={{
                          background: aiGenerationChip.background,
                          color: aiGenerationChip.color,
                          fontWeight: 600,
                          marginTop: 2,
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              </div>
            </GridItem>

            <Grid container className={classes.scrollContainer}>
              {/* Uncover self photo*/}
              {identity.self && (
                <Grid item className={classes.scrollItem}>
                  <div className={classes.idContainer}>
                    <CloudImage
                      className={classes.idImg}
                      src={identity.self}
                      defaultHeight="500px"
                      defaultWidth="350px"
                      alt="thumb"
                      onClick={() => window.open(identity.self, "_blank")}
                    />
                  </div>
                </Grid>
              )}

              {/* Uncover passport*/}
              {identity.passport && (
                <Grid item className={classes.scrollItem}>
                  <div className={classes.idContainer}>
                    <CloudImage
                      className={classes.idImg}
                      src={identity.passport}
                      defaultHeight="500px"
                      defaultWidth="350px"
                      alt="thumb"
                      onClick={() => window.open(identity.passport, "_blank")}
                    />
                  </div>
                </Grid>
              )}

              {/* Uncover ID front*/}
              {identity.idFront && (
                <Grid item className={classes.scrollItem}>
                  <div className={classes.idContainer}>
                    <CloudImage
                      className={classes.idImg}
                      src={identity.idFront}
                      defaultHeight="500px"
                      defaultWidth="350px"
                      alt="thumb"
                      onClick={() => window.open(identity.idFront, "_blank")}
                    />
                  </div>
                </Grid>
              )}

              {/* Uncover ID back*/}
              {identity.idBack && (
                <Grid item className={classes.scrollItem}>
                  <div className={classes.idContainer}>
                    <CloudImage
                      className={classes.idImg}
                      src={identity.idBack}
                      defaultHeight="500px"
                      defaultWidth="350px"
                      alt="thumb"
                      onClick={() => window.open(identity.idBack, "_blank")}
                    />
                  </div>
                </Grid>
              )}

              {godFatheridentity && (
                <Grid item className={classes.scrollItem}>
                  <div className={classes.postDetails}>
                    <p>
                      Godfather : {godFatheridentity.firstName}{" "}
                      {godFatheridentity.lastName} ({godFatheridentity.userName}
                      )
                    </p>
                  </div>
                </Grid>
              )}

              {/* Godfather self photo*/}
              {godFatheridentity && identity.self && (
                <Grid item className={classes.scrollItem}>
                  <div className={classes.idContainer}>
                    <CloudImage
                      className={classes.idImg}
                      src={godFatheridentity.self}
                      defaultHeight="500px"
                      defaultWidth="350px"
                      alt="thumb"
                      onClick={() =>
                        window.open(godFatheridentity.self, "_blank")
                      }
                    />
                  </div>
                </Grid>
              )}

              {/* Godfather ID Passport*/}
              {godFatheridentity && godFatheridentity.passport && (
                <Grid item className={classes.scrollItem}>
                  <div className={classes.idContainer}>
                    <CloudImage
                      className={classes.idImg}
                      src={godFatheridentity.passport}
                      defaultHeight="500px"
                      defaultWidth="350px"
                      alt="thumb"
                      onClick={() =>
                        window.open(godFatheridentity.passport, "_blank")
                      }
                    />
                  </div>
                </Grid>
              )}

              {/* Godfather ID Front*/}
              {godFatheridentity && godFatheridentity.idFront && (
                <Grid item className={classes.scrollItem}>
                  <div className={classes.idContainer}>
                    <CloudImage
                      className={classes.idImg}
                      src={godFatheridentity.idFront}
                      defaultHeight="500px"
                      defaultWidth="350px"
                      alt="thumb"
                      onClick={() =>
                        window.open(godFatheridentity.idFront, "_blank")
                      }
                    />
                  </div>
                </Grid>
              )}

              {/* Godfather ID back*/}
              {godFatheridentity && godFatheridentity.idBack && (
                <Grid item className={classes.scrollItem}>
                  <div className={classes.idContainer}>
                    <CloudImage
                      className={classes.idImg}
                      src={godFatheridentity.idBack}
                      defaultHeight="500px"
                      defaultWidth="350px"
                      alt="thumb"
                      onClick={() =>
                        window.open(godFatheridentity.idBack, "_blank")
                      }
                    />
                  </div>
                </Grid>
              )}

              {(identity.idFront || identity.passport) && !postsLoaded && (
                <Grid item className={classes.scrollItem}>
                  <Button
                    className={classes.gridButton}
                    onClick={showPosts}
                    variant="contained"
                  >
                    Show posts
                  </Button>
                </Grid>
              )}

              {posts.map((post) => (
                <Grid item className={classes.scrollItem}>
                  <div className={classes.idContainer}>
                    <CloudImage
                      className={classes.idImg}
                      src={post.src.normal}
                      defaultHeight="500px"
                      defaultWidth="350px"
                      alt="thumb"
                      onClick={() => window.open(post.src.normal, "_blank")}
                    />
                  </div>
                </Grid>
              ))}
            </Grid>

            <GridItem xs={12} sm={12} md={12}>
              <div className={classes.postDetails}>
                <div className={classes.posTopRight} key={identity.status}>
                  {
                    status[
                      identity.status
                        ? identity.status
                        : "identityNotYetAccepted"
                    ]
                  }
                </div>

                {identity.response && (
                  <p className={classes.colorRed}>{identity.response}</p>
                )}

                {(identity.status === "identityNotYetAccepted" ||
                  identity.status === "") && (
                  <>
                    <FormControl className={classes.formControl}>
                      <InputLabel>Responses</InputLabel>
                      <Select
                        onChange={handleResponseChange}
                        value={responseSelected}
                      >
                        {responses.map((response) => (
                          <MenuItem key={response.value} value={response.value}>
                            {response.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {responseSelected == "personalizedResponse" && (
                      <TextField
                        className={classes.descriptionTextField}
                        onChange={(e) => {
                          setResponseDetails(e.target.value);
                        }}
                        label="Response Details"
                        type="text"
                        variant="outlined"
                        value={responseDetails}
                      />
                    )}

                    {errorMessage && (
                      <p className={classes.errorMessage}>{errorMessage}</p>
                    )}

                    <Box
                      display="flex"
                      flexDirection="row"
                      justifyContent="space-between"
                    >
                      <Box>
                        <Button
                          className={classes.successButton}
                          onClick={() => {
                            sendResponse(identity._id, "identityAccepted");
                          }}
                          variant="contained"
                        >
                          Accept
                        </Button>

                        <Button
                          className={classes.warningButton}
                          onClick={() => {
                            sendResponse(identity._id, "identityNotAccepted");
                          }}
                          variant="contained"
                        >
                          Refuse
                        </Button>
                      </Box>

                      <Box>
                        <Button
                          className={classes.dangerButton}
                          onClick={handleSuspendUser}
                          variant="contained"
                        >
                          Block user
                        </Button>
                        <Button
                          className={classes.dangerButton}
                          onClick={handleDeleteUser}
                          variant="contained"
                        >
                          Delete user
                        </Button>
                      </Box>
                    </Box>
                  </>
                )}
              </div>
            </GridItem>
          </GridContainer>
        </Box>
      )}
    </>
  );
}
